import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, Users, X, Building2, Phone, Mail, User, MapPin, FileText, UserPlus, MessageSquare, Send, Receipt, Package, Clock, ChevronRight, ChevronDown, Briefcase, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { usePermissions, PermissionGate } from '../../context/PermissionContext';
import { CustomerTransactionSummary, CustomerPaymentsSection, CustomerOrdersSection } from './customers/CustomerPaymentsSection';
import { RecordPaymentDialog } from './customers/RecordPaymentDialog';
import { CustomerTimeline } from './customers/CustomerTimeline';
import { useAuth } from '../../context/AuthContext';
import { isFullAdmin } from '../tax/taxDisplay';
import { ExemptionForm, CERTS } from '../tax/TaxControls';
import { ExemptionSummary } from '../tax/TaxBadges';
import { HelpLink } from './HelpLink';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_TERMS = [
  { id: 'due_on_receipt', name: 'Due on Receipt' },
  { id: 'net_15', name: 'NET 15' },
  { id: 'net_30', name: 'NET 30' },
  { id: 'net_45', name: 'NET 45' },
];

const TAX_PREFERENCES = [
  { id: 'taxable', name: 'Taxable' },
  { id: 'tax_exempt', name: 'Tax Exempt' },
];

const BUSINESS_TYPES = [
  { id: 'interior_designer', name: 'Interior Designer' },
  { id: 'architect', name: 'Architect' },
  { id: 'contractor', name: 'Contractor' },
  { id: 'builder', name: 'Builder' },
  { id: 'dealer', name: 'Dealer' },
  { id: 'retailer', name: 'Retailer' },
  { id: 'custom_av_integrator', name: 'Custom AV Integrator' },
  { id: 'other', name: 'Other' },
];

const emptyCustomer = {
  primary_contact: '',
  company_name: '',
  display_name: '',
  email: '',
  work_phone: '',
  mobile_phone: '',
  is_trade: false,
  business_type: '',
  tax_preference: 'taxable',
  tax_rate: 8.875,
  payment_terms: 'due_on_receipt',
  discount_tier_id: '',
  assigned_to: '',  // Sales rep user ID
  billing_address: { street: '', city: '', state: '', zip_code: '', country: 'USA' },
  shipping_address: { street: '', city: '', state: '', zip_code: '', country: 'USA' },
  contact_persons: [],
  notes: '',
  comments: []
};

const emptyContactPerson = {
  first_name: '',
  last_name: '',
  email: '',
  work_phone: '',
  mobile_phone: '',
  designation: '',
  department: ''
};

export const CustomersTab = ({ token }) => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyCustomer);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [nextCustomerNumber, setNextCustomerNumber] = useState('');
  
  // Transactions data
  const [transactions, setTransactions] = useState({ quotes: [], invoices: [], sales_orders: [], payments: [], orders: [], summary: null });
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  
  // Comments
  const [newComment, setNewComment] = useState('');
  
  // Emails
  const [emails, setEmails] = useState([]);
  
  // New Transaction dropdown
  const [showTransactionDropdown, setShowTransactionDropdown] = useState(false);
  const transactionDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (transactionDropdownRef.current && !transactionDropdownRef.current.contains(event.target)) {
        setShowTransactionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchDiscountTiers();
    fetchSalesReps();
  }, [statusFilter]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/admin/customers`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountTiers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/discount-tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountTiers(response.data);
    } catch (error) {
      console.error('Failed to fetch discount tiers:', error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      // Fetch company users who can be assigned customers (sales roles)
      const response = await axios.get(`${API_URL}/admin/company-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to users with sales-related roles
      const salesRoles = ['role_sales_rep', 'role_sales_manager', 'role_admin', 'role_super_admin'];
      const reps = response.data.filter(user => 
        user.is_active !== false && 
        (salesRoles.includes(user.role_id) || user.is_admin)
      );
      setSalesReps(reps);
    } catch (error) {
      console.error('Failed to fetch sales reps:', error);
    }
  };

  const fetchNextCustomerNumber = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextCustomerNumber(response.data.next_number);
    } catch (error) {
      console.error('Failed to fetch next customer number:', error);
    }
  };

  const fetchCustomerTransactions = async (customerId) => {
    setLoadingTransactions(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/customers/${customerId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions({
        quotes: data.quotes || [],
        invoices: data.invoices || [],
        sales_orders: data.sales_orders || [],
        payments: data.payments || [],
        orders: data.orders || [],
        summary: data.summary || null
      });
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const openCreateModal = async () => {
    setEditingCustomer(null);
    setFormData(emptyCustomer);
    setActiveTab('overview');
    setTransactions({ quotes: [], invoices: [], sales_orders: [], payments: [], orders: [], summary: null });
    setEmails([]);
    await fetchNextCustomerNumber();
    setShowModal(true);
  };

  const openEditModal = async (customer) => {
    setEditingCustomer(customer);
    setFormData({
      primary_contact: customer.primary_contact || '',
      company_name: customer.company_name || '',
      display_name: customer.display_name || '',
      email: customer.email || '',
      work_phone: customer.work_phone || '',
      mobile_phone: customer.mobile_phone || '',
      is_trade: customer.is_trade || false,
      business_type: customer.business_type || '',
      tax_preference: customer.tax_preference || 'taxable',
      tax_rate: customer.tax_rate || 8.875,
      payment_terms: customer.payment_terms || 'due_on_receipt',
      discount_tier_id: customer.discount_tier_id || '',
      assigned_to: customer.assigned_to || '',
      billing_address: customer.billing_address || { street: '', city: '', state: '', zip_code: '', country: 'USA' },
      shipping_address: customer.shipping_address || { street: '', city: '', state: '', zip_code: '', country: 'USA' },
      contact_persons: customer.contact_persons || [],
      notes: customer.notes || '',
      comments: customer.comments || []
    });
    setActiveTab('timeline');
    setShowModal(true);
    
    // Fetch transactions and emails for this customer
    if (customer.email) {
      fetchCustomerTransactions(customer.id);
      fetchCustomerEmails(customer.email);
    }
  };

  const fetchCustomerEmails = async (customerEmail) => {
    try {
      const response = await axios.get(`${API_URL}/admin/emails?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter emails for this customer
      const customerEmails = response.data.filter(email => 
        email.to_email?.toLowerCase() === customerEmail?.toLowerCase()
      );
      setEmails(customerEmails);
    } catch (error) {
      console.error('Error fetching emails:', error);
      setEmails([]);
    }
  };

  const saveCustomer = async () => {
    if (!formData.primary_contact || !formData.email) {
      toast.error('Primary Contact and Email are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(`${API_URL}/admin/customers/${editingCustomer.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer updated');
      } else {
        await axios.post(`${API_URL}/admin/customers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer created');
      }
      fetchCustomers();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API_URL}/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const addContactPerson = () => {
    setFormData({
      ...formData,
      contact_persons: [...formData.contact_persons, { ...emptyContactPerson, id: Date.now().toString() }]
    });
  };

  const updateContactPerson = (index, field, value) => {
    const updated = [...formData.contact_persons];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, contact_persons: updated });
  };

  const removeContactPerson = (index) => {
    const updated = formData.contact_persons.filter((_, i) => i !== index);
    setFormData({ ...formData, contact_persons: updated });
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    const comment = {
      id: Date.now().toString(),
      text: newComment,
      author: 'Admin',
      created_at: new Date().toISOString()
    };
    
    const updatedComments = [...(formData.comments || []), comment];
    setFormData({ ...formData, comments: updatedComments });
    setNewComment('');
    
    // Auto-save comment if editing
    if (editingCustomer) {
      try {
        await axios.put(`${API_URL}/admin/customers/${editingCustomer.id}`, {
          comments: updatedComments
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Comment added');
      } catch (error) {
        toast.error('Failed to save comment');
      }
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.primary_contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.customer_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Get display name for header
  const getDisplayHeader = () => {
    if (editingCustomer) {
      return formData.display_name || formData.primary_contact || editingCustomer.customer_number;
    }
    return `New Customer: ${nextCustomerNumber}`;
  };

  return (
    <div data-testid="customers-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Customer Management</h2>
          <p className="text-neutral-500 text-sm">Manage customers, contacts, and billing preferences</p>
        </div>
        <PermissionGate module="crm" level="full">
        <Button onClick={openCreateModal} className="btn-primary" data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by name, company, email, or customer #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-neutral-200 rounded-lg text-sm"
          data-testid="customer-status-filter"
        >
          <option value="">All Customers</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Customers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <Users className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No customers found</p>
          <PermissionGate module="crm" level="full">
          <Button onClick={openCreateModal} className="mt-4" variant="outline">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Customer
          </Button>
          </PermissionGate>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Customer #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Primary Contact</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Phone</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Payment Terms</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Price List</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <tr key={customer.id} className="border-b last:border-0 hover:bg-neutral-50 cursor-pointer" onClick={() => openEditModal(customer)} data-testid={`customer-row-${customer.customer_number}`}>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-amber-600">
                    <div className="flex items-center gap-2">
                      {customer.customer_number}
                      {customer.is_trade && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                          Trade
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{customer.primary_contact}</p>
                    {customer.display_name && customer.display_name !== customer.primary_contact && (
                      <p className="text-xs text-neutral-400">Display: {customer.display_name}</p>
                    )}
                    {customer.assigned_to && (
                      <p className="text-xs text-blue-500 flex items-center gap-1 mt-0.5">
                        <User className="w-3 h-3" />
                        {salesReps.find(r => r.id === customer.assigned_to)?.name || 
                         salesReps.find(r => r.id === customer.assigned_to)?.email || 
                         'Assigned'}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">{customer.company_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-neutral-600">{customer.email}</td>
                  <td className="px-4 py-3 text-sm text-neutral-600">{customer.work_phone || customer.mobile_phone || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-neutral-100 rounded text-xs font-medium">
                      {PAYMENT_TERMS.find(t => t.id === customer.payment_terms)?.name || customer.payment_terms}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm ${customer.discount_tier_name ? 'text-amber-600 font-medium' : 'text-neutral-400'}`}>
                      {customer.discount_tier_name || 'Standard'}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <PermissionGate module="crm" level="edit">
                      <Button size="sm" variant="outline" onClick={() => openEditModal(customer)} data-testid={`edit-customer-${customer.customer_number}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      </PermissionGate>
                      <PermissionGate module="crm" level="full">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => deleteCustomer(customer.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-customer-${customer.customer_number}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Customer Modal - Full Page Style */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-[1400px] max-h-[95vh] overflow-hidden flex flex-col p-0">
          {/* Header with Display Name */}
          <div className="bg-neutral-900 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-xl font-semibold">{getDisplayHeader()}</h2>
                  {editingCustomer && (
                    <p className="text-neutral-400 text-sm mt-1">
                      {editingCustomer.customer_number} {editingCustomer.company_name && `• ${editingCustomer.company_name}`}
                      {editingCustomer.is_trade && (
                        <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">
                          Trade Account
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* New Transaction Dropdown */}
                {editingCustomer && (
                  <div className="relative" ref={transactionDropdownRef}>
                    <Button
                      onClick={() => setShowTransactionDropdown(!showTransactionDropdown)}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Transaction
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showTransactionDropdown ? 'rotate-180' : ''}`} />
                    </Button>
                    {showTransactionDropdown && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-50">
                        <button
                          onClick={() => {
                            // Store customer info for quote creation
                            const customerData = {
                              id: editingCustomer.id,
                              customer_number: editingCustomer.customer_number,
                              name: formData.display_name || formData.primary_contact,
                              email: formData.email,
                              phone: formData.work_phone || formData.mobile_phone,
                              company: formData.company_name,
                              is_trade: formData.is_trade,
                              tax_preference: formData.tax_preference,
                              tax_rate: formData.tax_rate,
                              payment_terms: formData.payment_terms,
                              discount_tier_id: formData.discount_tier_id,
                              billing_address: formData.billing_address,
                              shipping_address: formData.shipping_address
                            };
                            localStorage.setItem('newQuoteCustomer', JSON.stringify(customerData));
                            setShowModal(false);
                            setShowTransactionDropdown(false);
                            // Navigate to quotes tab with action=create
                            window.location.href = '/admin?tab=quotes&action=create';
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4 text-blue-600" />
                          Quote
                        </button>
                        <button
                          onClick={() => {
                            toast.info('Sales Orders are created from approved Quotes. Create a Quote first.');
                            setShowTransactionDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-2"
                        >
                          <Package className="w-4 h-4 text-purple-600" />
                          Sales Order
                        </button>
                        <button
                          onClick={() => {
                            toast.info('Invoices are created from Sales Orders. Create a Quote first.');
                            setShowTransactionDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-2"
                        >
                          <Receipt className="w-4 h-4 text-green-600" />
                          Invoice
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex gap-1 mt-4">
              {[...(editingCustomer ? ['timeline'] : []), 'overview', 'transactions', 'comments', 'mails'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab
                      ? 'bg-white text-neutral-900'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'timeline' && 'Timeline'}
                  {tab === 'overview' && 'Overview'}
                  {tab === 'transactions' && 'Transactions'}
                  {tab === 'comments' && 'Comments'}
                  {tab === 'mails' && 'Mails'}
                </button>
              ))}
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto bg-neutral-50">
            {activeTab === 'timeline' && editingCustomer && <CustomerTimeline customerId={editingCustomer.id} token={token} />}
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="p-6 space-y-6">
                {/* Top Section - 3 column grid */}
                <div className="grid grid-cols-4 gap-6">
                  {/* Left Column - Contact Info */}
                  <div className="col-span-2 space-y-6">
                    {/* Primary Info Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 p-5">
                      <h3 className="font-medium text-neutral-900 mb-4 flex items-center gap-2">
                        <User className="w-4 h-4" /> Contact Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Primary Contact *</Label>
                          <Input
                            value={formData.primary_contact}
                            onChange={(e) => setFormData({ ...formData, primary_contact: e.target.value })}
                            placeholder="John Smith"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Company Name</Label>
                          <Input
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            placeholder="Acme Corp"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Display As</Label>
                          <div className="relative mt-1">
                            <select
                              value={formData.display_name}
                              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                              className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm appearance-none bg-white"
                            >
                              <option value="">-- Select --</option>
                              {formData.primary_contact && (
                                <option value={formData.primary_contact}>{formData.primary_contact}</option>
                              )}
                              {formData.company_name && (
                                <option value={formData.company_name}>{formData.company_name}</option>
                              )}
                              {formData.primary_contact && formData.company_name && (
                                <option value={`${formData.primary_contact} - ${formData.company_name}`}>
                                  {formData.primary_contact} - {formData.company_name}
                                </option>
                              )}
                            </select>
                            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 rotate-90 pointer-events-none" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Email Address *</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="john@acme.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Work Phone</Label>
                          <Input
                            value={formData.work_phone}
                            onChange={(e) => setFormData({ ...formData, work_phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Mobile Phone</Label>
                          <Input
                            value={formData.mobile_phone}
                            onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                            placeholder="(555) 987-6543"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Address Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 p-5">
                      <h3 className="font-medium text-neutral-900 mb-4 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Addresses
                      </h3>
                      <div className="grid grid-cols-2 gap-6">
                        {/* Billing Address */}
                        <div>
                          <h4 className="text-sm font-medium text-neutral-700 mb-3">Billing Address</h4>
                          <div className="space-y-2">
                            <Input
                              value={formData.billing_address?.street || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                billing_address: { ...formData.billing_address, street: e.target.value }
                              })}
                              placeholder="Street Address"
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                value={formData.billing_address?.city || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  billing_address: { ...formData.billing_address, city: e.target.value }
                                })}
                                placeholder="City"
                              />
                              <Input
                                value={formData.billing_address?.state || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  billing_address: { ...formData.billing_address, state: e.target.value }
                                })}
                                placeholder="State"
                              />
                              <Input
                                value={formData.billing_address?.zip_code || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  billing_address: { ...formData.billing_address, zip_code: e.target.value }
                                })}
                                placeholder="ZIP"
                              />
                            </div>
                          </div>
                        </div>
                        {/* Shipping Address */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-neutral-700">Shipping Address</h4>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  formData.billing_address?.street === formData.shipping_address?.street &&
                                  formData.billing_address?.city === formData.shipping_address?.city &&
                                  formData.billing_address?.state === formData.shipping_address?.state &&
                                  formData.billing_address?.zip_code === formData.shipping_address?.zip_code &&
                                  formData.billing_address?.street !== ''
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      shipping_address: { ...formData.billing_address }
                                    });
                                  }
                                }}
                                className="w-4 h-4 text-amber-500 border-neutral-300 rounded focus:ring-amber-500"
                              />
                              <span className="text-xs text-neutral-600">Same as billing</span>
                            </label>
                          </div>
                          <div className="space-y-2">
                            <Input
                              value={formData.shipping_address?.street || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                shipping_address: { ...formData.shipping_address, street: e.target.value }
                              })}
                              placeholder="Street Address"
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                value={formData.shipping_address?.city || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  shipping_address: { ...formData.shipping_address, city: e.target.value }
                                })}
                                placeholder="City"
                              />
                              <Input
                                value={formData.shipping_address?.state || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  shipping_address: { ...formData.shipping_address, state: e.target.value }
                                })}
                                placeholder="State"
                              />
                              <Input
                                value={formData.shipping_address?.zip_code || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  shipping_address: { ...formData.shipping_address, zip_code: e.target.value }
                                })}
                                placeholder="ZIP"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Settings */}
                  <div className="col-span-2 space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Customer Number */}
                      <div className="bg-white rounded-lg border border-neutral-200 p-5">
                        <Label className="text-sm font-medium text-neutral-600">Customer Number</Label>
                        <Input
                          value={editingCustomer?.customer_number || nextCustomerNumber}
                          disabled
                          className="mt-1 bg-neutral-100 font-mono text-lg"
                        />
                      </div>

                      {/* Notes */}
                      <div className="bg-white rounded-lg border border-neutral-200 p-5">
                        <Label className="text-sm font-medium text-neutral-600">Internal Notes</Label>
                        <Textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Notes about this customer..."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Billing Settings */}
                    <div className="bg-white rounded-lg border border-neutral-200 p-5">
                      <h3 className="font-medium text-neutral-900 mb-4">Billing Settings</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2" data-testid="customer-tax-section">
                          <Label className="text-sm font-medium text-neutral-600 flex items-center gap-2">Sales Tax Exemption <HelpLink topic="tax-exemptions" testId="help-link-customer-exemption" /></Label>
                          <p className="text-xs text-neutral-500 mt-1">Tax is calculated from each order's delivery/service address. Exemptions need a certificate on file and are set by an Admin below.</p>
                          {isFullAdmin(user) && editingCustomer?.id && <div className="mt-3 border rounded-md p-3"><ExemptionForm path={`/api/admin/tax/customers/${editingCustomer.id}/exemption`} current={editingCustomer.tax_exemption} token={token} onSaved={(r) => setEditingCustomer((c) => ({ ...c, tax_exemption: r.tax_exemption }))} testId="customer-exemption" /></div>}
                          {!isFullAdmin(user) && <div className="mt-3 border rounded-md p-3"><ExemptionSummary ex={editingCustomer?.tax_exemption} certNames={CERTS} testId="customer-exemption-summary" /></div>}
                        </div>
                        <div className="hidden">
                          <Label className="text-sm font-medium text-neutral-600">Tax Preference</Label>
                          <select
                            value={formData.tax_preference}
                            onChange={(e) => setFormData({ ...formData, tax_preference: e.target.value })}
                            className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                          >
                            {TAX_PREFERENCES.map(pref => (
                              <option key={pref.id} value={pref.id}>{pref.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="hidden">
                          <Label className="text-sm font-medium text-neutral-600">Tax Rate (%)</Label>
                          <Input
                            type="number"
                            step="0.001"
                            value={formData.tax_rate}
                            onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                            disabled={formData.tax_preference === 'tax_exempt'}
                            className={`mt-1 ${formData.tax_preference === 'tax_exempt' ? 'bg-neutral-100' : ''}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Payment Terms</Label>
                          <select
                            value={formData.payment_terms}
                            onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                            className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                          >
                            {PAYMENT_TERMS.map(term => (
                              <option key={term.id} value={term.id}>{term.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Price List</Label>
                          <select
                            value={formData.discount_tier_id}
                            onChange={(e) => setFormData({ ...formData, discount_tier_id: e.target.value })}
                            className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                          >
                            <option value="">Standard Pricing</option>
                            {discountTiers.map(tier => (
                              <option key={tier.id} value={tier.id}>{tier.name} ({tier.discount_percentage}% off)</option>
                            ))}
                          </select>
                        </div>
                        <PermissionGate module="user_management" level="view" fallback={null}>
                        <div>
                          <Label className="text-sm font-medium text-neutral-600">Assigned Sales Rep</Label>
                          <select
                            value={formData.assigned_to || ''}
                            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value || null })}
                            className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                            data-testid="assigned-to-select"
                          >
                            <option value="">Unassigned</option>
                            {salesReps.map(rep => (
                              <option key={rep.id} value={rep.id}>
                                {rep.name || rep.email} {rep.role_id && `(${rep.role_id.replace('role_', '').replace(/_/g, ' ')})`}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-neutral-500 mt-1">
                            Assign this customer to a sales representative
                          </p>
                        </div>
                        </PermissionGate>
                      </div>
                    </div>

                    {/* Trade Account Settings */}
                    <div className="bg-white rounded-lg border border-neutral-200 p-5">
                      <h3 className="font-medium text-neutral-900 mb-4 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Trade Account
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-medium text-neutral-700">Trade Account Status</Label>
                            <p className="text-xs text-neutral-500">Enable trade pricing and features for this customer</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, is_trade: !formData.is_trade })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              formData.is_trade ? 'bg-amber-500' : 'bg-neutral-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.is_trade ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        
                        {formData.is_trade && (
                          <div>
                            <Label className="text-sm font-medium text-neutral-600">Business Type</Label>
                            <select
                              value={formData.business_type}
                              onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                              className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                            >
                              <option value="">Select Business Type</option>
                              {BUSINESS_TYPES.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {formData.is_trade && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-amber-700">
                              <BadgeCheck className="w-4 h-4" />
                              <span className="text-sm font-medium">Trade Benefits Active</span>
                            </div>
                            <p className="text-xs text-amber-600 mt-1">
                              This customer has access to trade pricing, priority support, and dedicated project coordination.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Persons - Full Width */}
                <div className="bg-white rounded-lg border border-neutral-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-neutral-900 flex items-center gap-2">
                      <Users className="w-4 h-4" /> Contact Persons
                    </h3>
                    <Button size="sm" variant="outline" onClick={addContactPerson}>
                      <UserPlus className="w-4 h-4 mr-1" /> Add Contact
                    </Button>
                  </div>
                  
                  {formData.contact_persons.length === 0 ? (
                    <p className="text-neutral-500 text-sm text-center py-4">No additional contacts</p>
                  ) : (
                    <div className="space-y-2 overflow-x-auto">
                      <div className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_1fr_40px] gap-3 px-2 text-xs font-medium text-neutral-500 uppercase min-w-[900px]">
                        <span>First Name</span>
                        <span>Last Name</span>
                        <span>Email</span>
                        <span>Work Phone</span>
                        <span>Mobile</span>
                        <span>Designation</span>
                        <span>Department</span>
                        <span></span>
                      </div>
                      {formData.contact_persons.map((contact, index) => (
                        <div key={contact.id || index} className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_1fr_40px] gap-3 items-center bg-neutral-50 p-2 rounded min-w-[900px]">
                          <Input value={contact.first_name} onChange={(e) => updateContactPerson(index, 'first_name', e.target.value)} placeholder="First" className="text-sm h-9" />
                          <Input value={contact.last_name} onChange={(e) => updateContactPerson(index, 'last_name', e.target.value)} placeholder="Last" className="text-sm h-9" />
                          <Input value={contact.email} onChange={(e) => updateContactPerson(index, 'email', e.target.value)} placeholder="Email" className="text-sm h-9" />
                          <Input value={contact.work_phone} onChange={(e) => updateContactPerson(index, 'work_phone', e.target.value)} placeholder="Work Phone" className="text-sm h-9" />
                          <Input value={contact.mobile_phone} onChange={(e) => updateContactPerson(index, 'mobile_phone', e.target.value)} placeholder="Mobile" className="text-sm h-9" />
                          <Input value={contact.designation} onChange={(e) => updateContactPerson(index, 'designation', e.target.value)} placeholder="Title" className="text-sm h-9" />
                          <Input value={contact.department} onChange={(e) => updateContactPerson(index, 'department', e.target.value)} placeholder="Dept" className="text-sm h-9" />
                          <Button size="sm" variant="ghost" onClick={() => removeContactPerson(index)} className="text-red-500 h-9 w-9 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Transaction History</h3>
                </div>
                
                {loadingTransactions ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" />
                    <p className="mt-2 text-neutral-500">Loading transactions...</p>
                  </div>
                ) : (
                  <>
                    <CustomerTransactionSummary summary={transactions.summary} />
                    <CustomerPaymentsSection payments={transactions.payments} />

                    {/* Quotes Section */}
                    <div className="bg-white rounded-lg border border-neutral-200">
                      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                        <h3 className="font-medium flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" /> Quotes
                          <span className="text-neutral-400 text-sm">({transactions.quotes.length})</span>
                        </h3>
                      </div>
                      {transactions.quotes.length === 0 ? (
                        <p className="text-neutral-500 text-sm text-center py-6">No quotes found</p>
                      ) : (
                        <div className="divide-y divide-neutral-100">
                          {transactions.quotes.map(quote => (
                            <div key={quote.id} className="px-5 py-3 flex items-center justify-between hover:bg-neutral-50">
                              <div>
                                <p className="font-medium text-sm">{quote.quote_number || `Quote #${quote.id.slice(0,8)}`}</p>
                                <p className="text-xs text-neutral-500">{formatDate(quote.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  quote.status === 'signed' ? 'bg-green-100 text-green-700' :
                                  quote.status === 'awaiting_approval' ? 'bg-amber-100 text-amber-700' :
                                  'bg-neutral-100 text-neutral-700'
                                }`}>{quote.status}</span>
                                <span className="font-medium">{formatCurrency(quote.total)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sales Orders Section */}
                    <div className="bg-white rounded-lg border border-neutral-200">
                      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                        <h3 className="font-medium flex items-center gap-2">
                          <Package className="w-4 h-4 text-purple-600" /> Sales Orders
                          <span className="text-neutral-400 text-sm">({transactions.sales_orders.length})</span>
                        </h3>
                      </div>
                      {transactions.sales_orders.length === 0 ? (
                        <p className="text-neutral-500 text-sm text-center py-6">No sales orders found</p>
                      ) : (
                        <div className="divide-y divide-neutral-100">
                          {transactions.sales_orders.map(so => (
                            <div key={so.id} className="px-5 py-3 flex items-center justify-between hover:bg-neutral-50">
                              <div>
                                <p className="font-medium text-sm">{so.so_number}</p>
                                <p className="text-xs text-neutral-500">{formatDate(so.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  so.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  so.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                                  so.status === 'converted_to_invoice' ? 'bg-blue-100 text-blue-700' :
                                  'bg-neutral-100 text-neutral-700'
                                }`}>{so.status?.replace(/_/g, ' ')}</span>
                                <span className="font-medium">{formatCurrency(so.total)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Invoices Section */}
                    <div className="bg-white rounded-lg border border-neutral-200">
                      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                        <h3 className="font-medium flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-green-600" /> Invoices
                          <span className="text-neutral-400 text-sm">({transactions.invoices.length})</span>
                        </h3>
                      </div>
                      {transactions.invoices.length === 0 ? (
                        <p className="text-neutral-500 text-sm text-center py-6">No invoices found</p>
                      ) : (
                        <div className="divide-y divide-neutral-100">
                          {transactions.invoices.map(invoice => (
                            <div key={invoice.id} className="px-5 py-3 flex items-center justify-between hover:bg-neutral-50">
                              <div>
                                <p className="font-medium text-sm">{invoice.invoice_number}</p>
                                <p className="text-xs text-neutral-500">{formatDate(invoice.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                  invoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                  'bg-neutral-100 text-neutral-700'
                                }`}>{invoice.status}</span>
                                <div className="text-right">
                                  <p className="font-medium">{formatCurrency(invoice.total)}</p>
                                  {invoice.balance_due > 0 && (
                                    <p className="text-xs text-red-600">Due: {formatCurrency(invoice.balance_due)}</p>
                                  )}
                                </div>
                                {invoice.balance_due > 0 && (
                                  <PermissionGate module="payments" level="full">
                                    <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setPaymentInvoice(invoice)} data-testid={`record-payment-btn-${invoice.id}`}>
                                      Record payment
                                    </Button>
                                  </PermissionGate>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <CustomerOrdersSection orders={transactions.orders} />
                    <RecordPaymentDialog
                      invoice={paymentInvoice}
                      open={Boolean(paymentInvoice)}
                      onOpenChange={(v) => !v && setPaymentInvoice(null)}
                      token={token}
                      onRecorded={() => editingCustomer?.id && fetchCustomerTransactions(editingCustomer.id)}
                    />
                  </>
                )}
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && (
              <div className="p-6">
                <div className="bg-white rounded-lg border border-neutral-200">
                  <div className="px-5 py-4 border-b border-neutral-200">
                    <h3 className="font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Comments & Notes
                    </h3>
                  </div>
                  
                  {/* Add Comment */}
                  <div className="px-5 py-4 border-b border-neutral-100">
                    <div className="flex gap-3">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={2}
                        className="flex-1"
                      />
                      <Button onClick={addComment} disabled={!newComment.trim()} className="self-end">
                        Add
                      </Button>
                    </div>
                  </div>
                  
                  {/* Comments List */}
                  <div className="divide-y divide-neutral-100">
                    {(!formData.comments || formData.comments.length === 0) ? (
                      <p className="text-neutral-500 text-sm text-center py-8">No comments yet</p>
                    ) : (
                      formData.comments.slice().reverse().map((comment) => (
                        <div key={comment.id} className="px-5 py-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-sm">{comment.author}</span>
                            <span className="text-neutral-400 text-xs">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatDate(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700">{comment.text}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mails Tab */}
            {activeTab === 'mails' && (
              <div className="p-6">
                <div className="bg-white rounded-lg border border-neutral-200">
                  <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
                    <h3 className="font-medium flex items-center gap-2">
                      <Send className="w-4 h-4" /> Email History ({emails.length})
                    </h3>
                  </div>
                  {emails.length === 0 ? (
                    <div className="py-12 text-center">
                      <Mail className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                      <p className="text-neutral-500">No emails sent to this customer yet</p>
                      <p className="text-neutral-400 text-sm mt-1">
                        Email history will appear here when the system sends notifications
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-neutral-100">
                      {emails.map((email) => (
                        <div key={email.id} className="px-5 py-4 hover:bg-neutral-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  email.status === 'sent' ? 'bg-green-100 text-green-800' : 
                                  email.status === 'failed' ? 'bg-red-100 text-red-800' : 
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {email.status || 'pending'}
                                </span>
                                <span className="text-xs text-neutral-400 capitalize">
                                  {email.template_name?.replace(/_/g, ' ') || 'Custom'}
                                </span>
                              </div>
                              <p className="font-medium text-neutral-900 text-sm">{email.subject}</p>
                              <p className="text-xs text-neutral-500 mt-1">
                                To: {email.to_email}
                              </p>
                              {email.error && (
                                <p className="text-xs text-red-500 mt-1">
                                  Error: {email.error}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-neutral-400">
                                {email.sent_at ? new Date(email.sent_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </p>
                              {email.reference_type && (
                                <p className="text-xs text-neutral-400 mt-1 capitalize">
                                  {email.reference_type}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 bg-white border-t border-neutral-200">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveCustomer} disabled={saving} className="btn-primary" data-testid="save-customer-btn">
              {saving ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Create Customer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersTab;
